"use client"

import { useState, useEffect } from "react"
import { apiService } from "../../services/apiService"
import { Link } from "react-router-dom"

const Dashboard = () => {
  const [stats, setStats] = useState({
    assigned_students: 0,
    pending_assignments: 0,
    unread_messages: 0,
    completed_courses: 0
  })
  const [recentActivities, setRecentActivities] = useState([])
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setIsLoading(true)
      setError("")

      // Get dashboard stats
      const dashboardStats = await apiService.getDashboardStats()
      setStats({
        assigned_students: dashboardStats.assigned_students || 0,
        pending_assignments: dashboardStats.pending_assignments || 0,
        unread_messages: dashboardStats.unread_messages || 0,
        completed_courses: dashboardStats.completed_courses || 0
      })

      // Get recent activities (combining assignments and messages)
      const [assignments, messages] = await Promise.all([
        apiService.getAssignments(),
        apiService.getMessages()
      ])

      const activities = [
        ...assignments.map(assignment => ({
          id: assignment.id,
          activity: `New assignment submitted by ${assignment.student_name}`,
          time: new Date(assignment.submitted_at).toLocaleString()
        })),
        ...messages.map(message => ({
          id: message.id,
          activity: `Message received from ${message.sender_name}`,
          time: new Date(message.created_at).toLocaleString()
        }))
      ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 5)

      setRecentActivities(activities)

      // Get upcoming deadlines (from assignments)
      const deadlines = assignments
        .filter(assignment => !assignment.is_completed)
        .map(assignment => ({
          id: assignment.id,
          title: `Review ${assignment.title}`,
          deadline: new Date(assignment.due_date).toLocaleDateString()
        }))
        .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
        .slice(0, 3)

      setUpcomingDeadlines(deadlines)
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      setError(error.message || 'Failed to load dashboard data')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return <div className="loading">Loading dashboard data...</div>
  }

  if (error) {
    return <div className="error-message">{error}</div>
  }

  return (
    <div className="dashboard">
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="material-icons">people</i>
          </div>
          <div className="stat-info">
            <h3>{stats.assigned_students}</h3>
            <p>Assigned Students</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="material-icons">assignment</i>
          </div>
          <div className="stat-info">
            <h3>{stats.pending_assignments}</h3>
            <p>Pending Assignments</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="material-icons">message</i>
          </div>
          <div className="stat-info">
            <h3>{stats.unread_messages}</h3>
            <p>Unread Messages</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="material-icons">school</i>
          </div>
          <div className="stat-info">
            <h3>{stats.completed_courses}</h3>
            <p>Completed Courses</p>
          </div>
        </div>
      </div>

      <div className="dashboard-row">
        <div className="dashboard-col">
          <h2 className="section-title">Recent Activities</h2>
          <div className="card">
            <ul className="activity-list">
              {recentActivities.map((activity) => (
                <li key={activity.id} className="activity-item">
                  <div className="activity-content">
                    <p>{activity.activity}</p>
                    <span className="activity-time">{activity.time}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="dashboard-col">
          <h2 className="section-title">Upcoming Deadlines</h2>
          <div className="card">
            <ul className="deadline-list">
              {upcomingDeadlines.map((deadline) => (
                <li key={deadline.id} className="deadline-item">
                  <div className="deadline-content">
                    <h3 className="deadline-title">{deadline.title}</h3>
                    <p className="deadline-date">Due: {deadline.deadline}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="dashboard-row">
        <div className="dashboard-col">
          <h2 className="section-title">Quick Actions</h2>
          <div className="quick-actions">
            <Link to="/mentor/students" className="quick-action-card">
              <i className="material-icons">people</i>
              <span>View Students</span>
            </Link>
            <Link to="/mentor/assignments" className="quick-action-card">
              <i className="material-icons">assignment</i>
              <span>Check Assignments</span>
            </Link>
            <Link to="/mentor/messages" className="quick-action-card">
              <i className="material-icons">message</i>
              <span>Send Messages</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
